import { useState } from "react";
import { ArrowUpRight, MapPin, Briefcase, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import PageHeader from "@/components/shared/PageHeader";
import { Reveal, SectionHeading, Icon } from "@/components/common/motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { jobs, benefits, hiringSteps } from "@/data/site";
import { applyCareer } from "@/lib/api";

function ApplyDialog({ role, children }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", portfolio: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      toast.error("Please enter a valid name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await applyCareer({ ...form, role });
      toast.success(res.message || "Application received!");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", portfolio: "", message: "" });
    } catch {
      toast.error("Could not submit application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Apply — {role}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-4" data-testid="apply-form">
          <div className="grid gap-2">
            <Label htmlFor="ap-name">Full name *</Label>
            <Input id="ap-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" data-testid="apply-name" required />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="ap-email">Email *</Label>
              <Input id="ap-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" data-testid="apply-email" required />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ap-phone">Phone</Label>
              <Input id="ap-phone" value={form.phone} onChange={set("phone")} placeholder="+1 ..." data-testid="apply-phone" />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="ap-portfolio">Portfolio / LinkedIn</Label>
            <Input id="ap-portfolio" value={form.portfolio} onChange={set("portfolio")} placeholder="https://..." data-testid="apply-portfolio" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="ap-message">Why you&apos;re a great fit</Label>
            <Textarea id="ap-message" value={form.message} onChange={set("message")} rows={4} placeholder="Tell us a bit about yourself..." data-testid="apply-message" />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="apply-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Application"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Careers() {
  return (
    <>
      <SEO title="Careers" description="Join NeuroTriq TechLabs. Remote-first roles in engineering, AI, cloud, design and security with real growth and great people." path="/careers" />
      <PageHeader
        overline="Careers"
        title="Build what's next, with people who care"
        subtitle="We're a remote-first team of senior, curious builders. If you love craft and outcomes, you'll feel at home here."
      />

      {/* Benefits */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><SectionHeading overline="Why join us" title="Perks that actually matter" subtitle="We invest in your growth, wellbeing and the freedom to do your best work." /></Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-2xl border border-border bg-card p-6">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary"><Icon name={b.icon} className="h-5 w-5" /></span>
                  <h3 className="mt-5 font-display text-lg font-medium">{b.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Open roles */}
      <section className="bg-muted py-24 sm:py-32">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal><SectionHeading overline="Open roles" title="Come build with us" subtitle="Don't see the perfect role? Apply anyway — we're always meeting great people." /></Reveal>
          <div className="mt-12 space-y-4">
            {jobs.map((j, i) => (
              <Reveal key={j.id} delay={i * 0.05}>
                <div className="group grid gap-4 rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/40 sm:grid-cols-[1fr_auto] sm:items-center" data-testid={`job-${j.id}`}>
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display text-xl font-medium">{j.title}</h3>
                      <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">{j.team}</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{j.desc}</p>
                    <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" /> {j.type}</span>
                      <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {j.location}</span>
                    </div>
                  </div>
                  <ApplyDialog role={j.title}>
                    <button className="inline-flex items-center justify-center gap-2 rounded-full bg-secondary px-5 py-3 text-sm font-medium text-secondary-foreground transition-colors hover:bg-primary hover:text-primary-foreground" data-testid={`apply-${j.id}`}>
                      Apply now <ArrowUpRight className="h-4 w-4" />
                    </button>
                  </ApplyDialog>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Hiring process */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><SectionHeading overline="Hiring process" title="Fast, fair and human" subtitle="No trick questions, no endless rounds — just a respectful process built around real work." /></Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {hiringSteps.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-border bg-card p-6">
                  <span className="font-display text-3xl font-semibold text-primary/25">{s.step}</span>
                  <h3 className="mt-3 font-display text-base font-medium">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
